import { useCallback, useEffect, useRef } from 'react';

import {
  applyManualOrder,
  getManualOrderMenuState,
  getManualOrderRowId,
  getManualOrderSignature,
  loadManualOrderIds,
  type ManualOrderConfig,
  type ManualOrderMenuState,
  type ManualOrderMoveAction,
  mapManualOrderItems,
  reorderByAction,
  reorderByDrag,
  saveManualOrderIds,
} from './manualOrder.utils';

type UseManualOrderArgs<T> = {
  config?: ManualOrderConfig<T>;
  items?: T[];
  onChange?: (items: T[]) => void;
  onEnsureCustomOrder?: () => void;
};

type UseManualOrderResult<T> = {
  handleDragReorder: (prevId: string, newId: string) => void;
  handleMoveAction: (action: ManualOrderMoveAction, rowId: string) => void;
  getMenuState: (rowId: string) => ManualOrderMenuState;
};

export const useManualOrder = <T>({
  config,
  items = [],
  onChange,
  onEnsureCustomOrder,
}: UseManualOrderArgs<T>): UseManualOrderResult<T> => {
  const getRowId = useCallback((row: T) => getManualOrderRowId(row, config?.getRowId), [config?.getRowId]);
  const lastAppliedScopeRef = useRef<string | null>(null);
  const lastAppliedItemSigRef = useRef<string>('');

  const commitOrder = useCallback(
    (ordered: T[]) => {
      if (!config || !onChange) {
        return;
      }

      const mapped = mapManualOrderItems(ordered, config.mapOrderedItem);
      const ids = mapped.map(getRowId).filter(Boolean);
      saveManualOrderIds(config.storageKey, config.scopeKey, ids);
      lastAppliedScopeRef.current = config.scopeKey;
      lastAppliedItemSigRef.current = getManualOrderSignature(mapped, getRowId);
      onChange(mapped);
    },
    [config, getRowId, onChange]
  );

  useEffect(() => {
    if (!config?.scopeKey || !items.length || !onChange) {
      return;
    }

    const storedIds = loadManualOrderIds(config.storageKey, config.scopeKey);
    if (!storedIds?.length) {
      lastAppliedScopeRef.current = config.scopeKey;
      lastAppliedItemSigRef.current = getManualOrderSignature(items, getRowId);
      return;
    }

    const applied = mapManualOrderItems(applyManualOrder(items, storedIds, getRowId), config.mapOrderedItem);
    const appliedSignature = getManualOrderSignature(applied, getRowId);
    const currentSignature = getManualOrderSignature(items, getRowId);
    const itemsChanged = currentSignature !== lastAppliedItemSigRef.current;
    const scopeChanged = lastAppliedScopeRef.current !== config.scopeKey;

    if (appliedSignature !== currentSignature && (itemsChanged || scopeChanged)) {
      lastAppliedScopeRef.current = config.scopeKey;
      lastAppliedItemSigRef.current = appliedSignature;
      onChange(applied);
      return;
    }

    lastAppliedScopeRef.current = config.scopeKey;
    lastAppliedItemSigRef.current = currentSignature;
  }, [config, getRowId, items, onChange]);

  const handleDragReorder = useCallback(
    (prevId: string, newId: string) => {
      if (!config || !items.length || !onChange) {
        return;
      }

      onEnsureCustomOrder?.();
      commitOrder(reorderByDrag(items, prevId, newId, getRowId));
    },
    [commitOrder, config, getRowId, items, onChange, onEnsureCustomOrder]
  );

  const handleMoveAction = useCallback(
    (action: ManualOrderMoveAction, rowId: string) => {
      if (!config || !items.length || !onChange) {
        return;
      }

      onEnsureCustomOrder?.();
      commitOrder(reorderByAction(items, rowId, action, getRowId));
    },
    [commitOrder, config, getRowId, items, onChange, onEnsureCustomOrder]
  );

  const getMenuState = useCallback((rowId: string) => getManualOrderMenuState(items, rowId, getRowId), [getRowId, items]);

  return {
    handleDragReorder,
    handleMoveAction,
    getMenuState,
  };
};

export type { ManualOrderConfig, ManualOrderMenuState, ManualOrderMoveAction };
